import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { OutreachTask } from "@/hooks/useOutreachTasks";

export interface PaginatedTasksParams {
  page: number;
  perPage: number;
  /** Inclusive ISO-8601 bounds on due_date. */
  dateFrom?: string;
  dateTo?: string;
  /** Filter to completed vs. incomplete tasks; omit for both. */
  completed?: boolean;
}

/** Server-side paginated outreach tasks for the Reports page's "Upcoming Tasks"
 *  table — only the current page's rows are ever pulled into memory. */
export function usePaginatedTasks(params: PaginatedTasksParams) {
  const { user } = useAuth();
  // See useProfile.ts — depend on the stable user id, not the object
  // reference, which changes on every Supabase token refresh.
  const userId = user?.id;
  const [tasks, setTasks] = useState<OutreachTask[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const requestId = useRef(0);

  const { page, perPage, dateFrom, dateTo, completed } = params;

  const fetchPage = useCallback(async () => {
    if (!userId) { setTasks([]); setTotalCount(0); setLoading(false); return; }
    const id = ++requestId.current;
    setLoading(true);

    let query = supabase.from("outreach_tasks").select("*", { count: "exact" });
    if (dateFrom) query = query.gte("due_date", dateFrom);
    if (dateTo) query = query.lte("due_date", dateTo);
    if (completed !== undefined) query = query.eq("completed", completed);
    query = query
      .order("due_date", { ascending: true })
      .range(page * perPage, page * perPage + perPage - 1) as any;

    const { data, count, error } = await query;
    if (id !== requestId.current) return;
    if (!error) {
      setTasks((data as OutreachTask[]) ?? []);
      setTotalCount(count ?? 0);
    }
    setLoading(false);
  }, [userId, page, perPage, dateFrom, dateTo, completed]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  return { tasks, totalCount, loading, refetch: fetchPage };
}
